// pages/profile/Notifications.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, Package, MessageSquare, Star,X, Zap, Heart, AlertTriangle, CreditCard, ChevronRight, Filter, MoreVertical } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import { useAuth } from '../../context/AuthContext';
import { useNotificationWebSocket } from '../../hooks/useNotificationWebSocket';
import { 
  getUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  deleteNotification,
  getUnreadCountNotifications
} from '../../services/api';
import { requestNotificationPermission, playNotificationSound } from '../../utils/notificationUtils';

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isConnected, subscribeToNotifications, markAsReadViaWebSocket, markAllAsReadViaWebSocket } = useNotificationWebSocket();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showToast, setShowToast] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    fetchNotifications(true);
    fetchUnreadCount();
    requestNotificationPermission();
    
    // Subscribe to WebSocket notifications
    const unsubscribe = subscribeToNotifications(
      handleNewNotification,
      handleNotificationReadUpdate,
      handleUnreadCountUpdate
    );
    
    return () => {
      unsubscribe();
    };
  }, [user]);

  
const handleNewNotification = (notification, unreadCount) => {
  console.log('🔔 New notification:', notification, 'Unread count:', unreadCount);
  
  // Add to list
  setNotifications(prev => [notification, ...prev]);
  
  // Update unread count
  setUnreadCount(unreadCount);
  
  // Show browser notification
  if (Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification.id?.toString(),
          requireInteraction: ['order', 'dispute', 'payment'].includes(notification.type)
      });
      
      browserNotification.onclick = () => {
          window.focus();
          handleNotificationClick(notification);
          browserNotification.close();
      };
  }
  
  // Play sound
  playNotificationSound(notification.type);
  
  // Show toast
  setShowToast(notification);
  setTimeout(() => setShowToast(null), 5000);
};


const handleNotificationReadUpdate = (notificationId, unreadCount) => {
  setNotifications(prev => 
      prev.map(notif => 
          notif.id === notificationId ? { ...notif, is_read: true } : notif
      )
  );
  setUnreadCount(unreadCount);
};

const handleUnreadCountUpdate = (count) => {
  setUnreadCount(count);
};

const handleAllNotificationsCleared = (unreadCount) => {
  setNotifications(prev => 
      prev.map(notif => ({ ...notif, is_read: true }))
  );
  setUnreadCount(unreadCount);
};

// Subscribe to WebSocket notifications
useEffect(() => {
  if (!user) return;
  
  const unsubscribe = subscribeToNotifications(
      handleNewNotification,
      handleNotificationReadUpdate,
      handleUnreadCountUpdate,
      handleAllNotificationsCleared
  );
  
  return () => {
      unsubscribe();
  };
}, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCountNotifications();
      if (response.success) {
        setUnreadCount(response.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setPage(1);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const limit = 20;
      const offset = reset ? 0 : (page - 1) * limit;
      const response = await getUserNotifications(limit, offset);
      
      if (response.success) {
        const newNotifications = response.data || [];
        
        if (reset) {
          setNotifications(newNotifications);
          setHasMore(newNotifications.length === limit);
        } else {
          setNotifications(prev => [...prev, ...newNotifications]);
          setHasMore(newNotifications.length === limit);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const getNotificationIcon = (type) => {
    const iconClass = "w-5 h-5";
    switch (type) {
      case 'order':
        return <Package className={`${iconClass} text-blue-500`} />;
      case 'message':
        return <MessageSquare className={`${iconClass} text-green-500`} />;
      case 'rating':
        return <Star className={`${iconClass} text-yellow-500`} />;
      case 'boost':
        return <Zap className={`${iconClass} text-purple-500`} />;
      case 'favorite':
        return <Heart className={`${iconClass} text-red-500`} />;
      case 'dispute':
        return <AlertTriangle className={`${iconClass} text-orange-500`} />;
      case 'payment':
        return <CreditCard className={`${iconClass} text-emerald-500`} />;
      default:
        return <Bell className={`${iconClass} text-gray-500`} />;
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read via WebSocket if connected
    if (!notification.is_read) {
      if (isConnected) {
        markAsReadViaWebSocket(notification.id);
      } else {
        handleMarkAsRead(notification.id);
      }
    }
    
    // Navigate based on notification type
    const { type, related_id, related_type, metadata } = notification;
    
    const navigationMap = {
      order: () => navigate(`/my-orders/${related_id || metadata?.order_id}`),
      message: () => navigate(`/chats/${related_id || metadata?.conversation_id}`),
      rating: () => {
        if (metadata?.listing_id) {
          navigate(`/listing/${metadata.listing_id}#reviews`);
        } else if (metadata?.seller_id) {
          navigate(`/seller/${metadata.seller_id}#reviews`);
        } else {
          navigate('/profile/ratings');
        }
      },
      boost: () => {
        if (related_id) {
          navigate(`/store/boost-history?boost_id=${related_id}`);
        } else if (metadata?.listing_id) {
          navigate(`/listing/${metadata.listing_id}`);
        } else {
          navigate('/store/boost-history');
        }
      },
      favorite: () => navigate(`/listing/${related_id || metadata?.listing_id}`),
      dispute: () => navigate(`/disputes/${related_id || metadata?.dispute_id}`),
      payment: () => navigate(`/wallet/transactions/${related_id || metadata?.transaction_id}`),
      system: () => {
        if (metadata?.url) navigate(metadata.url);
      }
    };
    
    const navigation = navigationMap[type];
    if (navigation) {
      navigation();
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await markNotificationAsRead(notificationId);
      if (response.success) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId ? { ...notif, is_read: true } : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        setShowActionsMenu(null);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      if (isConnected) {
        markAllAsReadViaWebSocket();
      }
      
      const response = await markAllNotificationsAsRead();
      if (response.success) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, is_read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const response = await deleteNotification(notificationId);
      if (response.success) {
        setNotifications(prev => 
          prev.filter(notif => notif.id !== notificationId)
        );
        setSelectedNotifications(prev => 
          prev.filter(id => id !== notificationId)
        );
        setShowActionsMenu(null);
        
        const deletedNotif = notifications.find(n => n.id === notificationId);
        if (deletedNotif && !deletedNotif.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      for (const notificationId of selectedNotifications) {
        await deleteNotification(notificationId);
      }
      setNotifications(prev => 
        prev.filter(notif => !selectedNotifications.includes(notif.id))
      );
      setSelectedNotifications([]);
      setSelectAll(false);
      fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting selected:', error);
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
    } else {
      const filtered = filteredNotifications.map(notif => notif.id);
      setSelectedNotifications(filtered);
    }
    setSelectAll(!selectAll);
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      setPage(prev => prev + 1);
      fetchNotifications(false);
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.is_read;
    if (filter === 'read') return notification.is_read;
    return true;
  });

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Toast Component
  const Toast = () => {
    if (!showToast) return null;
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:bottom-4 z-50 animate-slide-up">
        <div 
          className="bg-white rounded-xl shadow-lg border-l-4 border-orange-500 p-4 cursor-pointer"
          onClick={() => handleNotificationClick(showToast)}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              {getNotificationIcon(showToast.type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{showToast.title}</p>
              <p className="text-sm text-gray-600">{showToast.message}</p>
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowToast(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Toast Notifications */}
      <Toast />
      
      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <ChevronRight className="w-5 h-5 text-black transform rotate-180" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Notifications</h1>
            
            <div className="flex items-center space-x-2">
              {isConnected && (
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              )}
              {unreadCount > 0 && (
                <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Desktop Header */}
        <div className="hidden lg:block mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
                {isConnected && (
                  <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                    Live
                  </span>
                )}
              </div>
              <p className="text-gray-600 mt-2">
                {unreadCount > 0 
                  ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                  : 'All caught up!'
                }
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {selectedNotifications.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedNotifications.length})
                </button>
              )}
              
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark All as Read
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters - Mobile */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Filter className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-gray-700 font-medium text-sm">Filter:</span>
            </div>
            <div className="flex space-x-1">
              {['all', 'unread', 'read'].map((filterType) => (
                <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                    filter === filterType
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterType}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters - Desktop */}
        <div className="hidden lg:block bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Filter className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700 font-medium">Filter:</span>
              </div>
              <div className="flex space-x-2">
                {['all', 'unread', 'read'].map((filterType) => (
                  <button
                    key={filterType}
                    onClick={() => setFilter(filterType)}
                    className={`px-4 py-2 rounded-lg font-medium capitalize ${
                      filter === filterType
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filterType}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-600">Select all</span>
            </div>
          </div>
        </div>

         {/* Notifications List */}
         <div className="space-y-3">
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-xl shadow-sm border transition-all ${
                  !notification.is_read ? 'border-l-4 border-l-orange-500' : ''
                } ${expandedNotification === notification.id ? 'shadow-md' : ''}`}
              >
                <div className="p-4">
                  <div className="flex items-start">
                    {/* Selection Checkbox - Desktop */}
                    <div className="hidden lg:block mr-3 pt-1">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.includes(notification.id)}
                        onChange={() => handleSelectNotification(notification.id)}
                        className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                      />
                    </div>

                    {/* Notification Icon */}
                    <div className="mr-3 lg:mr-4">
                      <div className={`p-2 lg:p-3 rounded-full ${
                        !notification.is_read ? 'bg-orange-100' : 'bg-gray-100'
                      }`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>

                    {/* Notification Content */}
                    <div 
                      className="flex-1 cursor-pointer min-w-0"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <h3 className={`font-medium text-sm lg:text-base truncate ${
                              !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </h3>
                            <span className="text-xs lg:text-sm text-gray-500 whitespace-nowrap ml-2">
                              {formatTime(notification.created_at)}
                            </span>
                          </div>
                          
                          <p className="text-gray-600 text-sm lg:text-base mt-1">
                            {notification.message}
                          </p>
                          
                          {(expandedNotification === notification.id || window.innerWidth >= 768) && 
                           notification.metadata && (
                            <div className="mt-2 text-xs lg:text-sm text-gray-500 bg-gray-50 p-2 rounded">
                              {notification.metadata.details && (
                                <p>{notification.metadata.details}</p>
                              )}
                              {notification.metadata.amount && (
                                <p className="mt-1 font-medium">
                                  Amount: ₦{notification.metadata.amount.toLocaleString()}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Read Status Dot */}
                        <div className="flex items-center mt-2 lg:mt-0 lg:ml-4">
                          {!notification.is_read && (
                            <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-2 lg:mr-0"></span>
                          )}
                          
                          {/* Mobile Expand/Collapse Icon */}
                          <div className="lg:hidden ml-auto">
                            <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                              expandedNotification === notification.id ? 'transform rotate-90' : ''
                            }`} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions - Desktop */}
                    <div className="hidden lg:flex flex-col space-y-2 ml-4">
                      {!notification.is_read && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Mobile Actions Menu */}
                    <div className="lg:hidden relative ml-2">
                      <button
                        onClick={() => setShowActionsMenu(showActionsMenu === notification.id ? null : notification.id)}
                        className="p-2 text-gray-500 hover:text-gray-700"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      
                      {showActionsMenu === notification.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border z-10">
                          <div className="py-1">
                            {!notification.is_read && (
                              <button
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Check className="w-4 h-4 mr-3" />
                                Mark as read
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteNotification(notification.id)}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-3" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selection Checkbox - Mobile */}
                  <div className="lg:hidden flex items-center mt-3 pt-3 border-t">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.includes(notification.id)}
                      onChange={() => handleSelectNotification(notification.id)}
                      className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">Select</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-2xl shadow-sm p-8 lg:p-12 text-center">
              <div className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-4 lg:mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <Bell className="w-8 h-8 lg:w-12 lg:h-12 text-gray-400" />
              </div>
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-2">
                No notifications
              </h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6 text-sm lg:text-base">
                {filter === 'all'
                  ? "You're all caught up! No notifications at the moment."
                  : filter === 'unread'
                  ? 'No unread notifications. Great job keeping up!'
                  : 'No read notifications found.'
                }
              </p>
              {filter !== 'all' && (
                <button
                  onClick={() => setFilter('all')}
                  className="px-4 lg:px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm lg:text-base"
                >
                  View All Notifications
                </button>
              )}
            </div>
          )}
        </div>

        {/* Load More */}
        {filteredNotifications.length > 0 && hasMore && (
          <div className="text-center mt-8">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-4 lg:px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm lg:text-base disabled:opacity-50"
            >
              {loadingMore ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                  Loading...
                </div>
              ) : (
                'Load Older Notifications'
              )}
            </button>
          </div>
        )}
      </div>
      
      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden">
        <MobileBottomNav />
      </div>
      
      <Footer />
    </div>
  );
};

export default Notifications;